import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import { useAppContext } from '../../context/AppContext';

export default function DashboardLayout({ children }) {
  const location = useLocation();
  const { profile, activeDomain } = useAppContext();

  const navItems = [
    { name: 'Overview', path: '/dashboard', icon: 'Grid' },
    { name: 'Edit Profile', path: '/dashboard/profile', icon: 'User' },
    { name: 'My Devices', path: '/dashboard/devices', icon: 'Smartphone' },
    { name: 'Orders', path: '/dashboard/orders', icon: 'ShoppingBag' },
    { name: 'Custom Domain', path: '/dashboard/domain', icon: 'Globe' },
  ];

  const adminItems = [
    { name: 'Products', path: '/dashboard/admin/products', icon: 'Package' },
    { name: 'Devices Master', path: '/dashboard/admin/devices', icon: 'Cpu' },
    { name: 'Users', path: '/dashboard/admin/users', icon: 'Users' },
    { name: 'Orders', path: '/dashboard/admin/orders', icon: 'Clipboard' },
    { name: 'Restricted Domains', path: '/dashboard/admin/restrictions', icon: 'ShieldAlert' },
    { name: 'Settings', path: '/dashboard/admin/settings', icon: 'Settings' },
  ];

  if (!profile) return null;

  // Link to either custom domain or platform profile URL
  const viewCardUrl = activeDomain 
    ? `https://${activeDomain}` 
    : `/p/${profile.username}`;

  const isExternal = !!activeDomain;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar */}
          <div className="w-full lg:w-64 flex-shrink-0">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
              <div className="flex items-center gap-4 mb-6">
                <img
                  src={profile.avatar_url}
                  alt="Avatar"
                  className="w-12 h-12 rounded-full object-cover border border-gray-200"
                />
                <div className="min-w-0">
                  <h3 className="font-semibold text-gray-900 truncate">
                    {profile.first_name} {profile.last_name}
                  </h3>
                  {isExternal ? (
                    <a 
                      href={viewCardUrl} 
                      target="_blank" 
                      rel="noreferrer"
                      className="text-xs text-indigo-600 hover:underline flex items-center gap-1"
                    >
                      View Card
                      <SafeIcon name="ExternalLink" className="text-[10px]" />
                    </a>
                  ) : (
                    <Link 
                      to={viewCardUrl} 
                      target="_blank" 
                      className="text-xs text-indigo-600 hover:underline flex items-center gap-1"
                    >
                      View Card
                      <SafeIcon name="ExternalLink" className="text-[10px]" />
                    </Link>
                  )}
                </div>
              </div>

              <nav className="space-y-1">
                <p className="px-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Member</p>
                {navItems.map((item) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <Link
                      key={item.name}
                      to={item.path}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${isActive ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-gray-600 hover:bg-gray-50'}`}
                    >
                      <SafeIcon
                        name={item.icon}
                        className={isActive ? 'text-indigo-600' : 'text-gray-400'}
                      />
                      <span className="text-sm">{item.name}</span>
                    </Link>
                  );
                })}

                {profile.is_admin && (
                  <div className="pt-6">
                    <p className="px-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Admin</p>
                    {adminItems.map((item) => {
                      const isActive = location.pathname === item.path;
                      return (
                        <Link
                          key={item.name}
                          to={item.path}
                          className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${isActive ? 'bg-purple-50 text-purple-700 font-medium' : 'text-gray-600 hover:bg-gray-50'}`}
                        >
                          <SafeIcon
                            name={item.icon}
                            className={isActive ? 'text-purple-600' : 'text-gray-400'}
                          />
                          <span className="text-sm">{item.name}</span>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8">
              {children}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
